import axios, { AxiosRequestConfig } from 'axios';

export type WhatsappDirection = 'in' | 'out';
export type WhatsappMessageType = 'text' | 'image' | 'audio' | 'video' | 'document' | 'sticker' | 'location';
export type WhatsappStatus = 'pending' | 'sent' | 'delivered' | 'read' | 'failed';

export interface WhatsappMessage {
  id: number;
  wamid?: string | null;
  direction: WhatsappDirection;
  type: WhatsappMessageType;
  body: string | null;
  media_url?: string | null;
  media_mime?: string | null;
  filename?: string | null;
  status?: WhatsappStatus | null;
  error?: string | null;
  /** Marcas alternativas de «eliminado para todos» según cómo lo guarde el API. */
  deleted_at?: string | null;
  revoked_at?: string | null;
  from_user_id?: number | null;
  from_user_name?: string | null;
  from_user?: { id?: number; name?: string | null; mainImg?: string | null } | null;
  our_reaction?: string | null;
  our_reaction_user_id?: number | null;
  our_reaction_user_name?: string | null;
  reactions?: WhatsappReaction[] | null;
  template?: WhatsappTemplateInfo | null;
  created_at: string;
}

/** Plantilla (HSM) con la que se envió un mensaje saliente automático. */
export interface WhatsappTemplateInfo {
  name: string;
  language?: string | null;
  /** Variables en el orden en que se mandaron a Meta ({{1}}, {{2}}…). */
  params: string[];
  /** Cuerpo ya montado, si el API lo guarda. */
  text?: string | null;
}

export interface WhatsappReaction {
  emoji: string;
  direction?: WhatsappDirection;
  from_user_id?: number | null;
  from_user_name?: string | null;
  mainImg?: string | null;
  from_user?: { id?: number; name?: string | null; nombre?: string | null; mainImg?: string | null } | null;
}

export interface WhatsappEntidad {
  id?: number;
  nombre?: string | null;
  mainImg?: string | null;
}

export interface WhatsappConversation {
  id: number;
  wa_id: string;
  phone: string;
  name: string | null;
  entidad_id: number | null;
  entidad?: WhatsappEntidad | null;
  unread_count: number;
  last_message_at: string | null;
  session_open: boolean;
  session_expires_at: string | null;
  last_message: Pick<WhatsappMessage, 'id' | 'body' | 'type' | 'direction' | 'status' | 'created_at' | 'our_reaction' | 'our_reaction_user_id' | 'our_reaction_user_name' | 'deleted_at' | 'revoked_at'> | null;
}

export interface WhatsappConversationPage {
  data: WhatsappConversation[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

export interface WhatsappMessagePage {
  data: WhatsappMessage[];
  has_more: boolean;
}

export interface WhatsappMediaSource {
  url: string;
  revoke: boolean;
}

const MEDIA_TYPES = new Set(['image', 'audio', 'video', 'document', 'sticker']);

export const whatsappMessageHasMedia = (message: Pick<WhatsappMessage, 'type'>) =>
  MEDIA_TYPES.has(message.type);

/**
 * Admite tanto `template: { name, params }` como los campos planos
 * `template_name` / `template_params`, y variables en array u objeto.
 */
const normalizeTemplate = (message: WhatsappMessage): WhatsappTemplateInfo | null => {
  const raw = (message as { template?: unknown; template_name?: unknown }).template
    ?? (message as { template_name?: unknown }).template_name;
  if (!raw) return null;

  const source = (typeof raw === 'string' ? { name: raw } : raw) as Record<string, unknown>;
  const name = String(source.name || source.template_name || '').trim();
  if (!name) return null;

  const rawParams = source.params ?? source.parameters ?? source.variables
    ?? (message as { template_params?: unknown }).template_params ?? [];
  const list = Array.isArray(rawParams)
    ? rawParams
    : (rawParams && typeof rawParams === 'object' ? Object.values(rawParams) : []);
  const params = list
    .map((item) => {
      if (item && typeof item === 'object') {
        const entry = item as { text?: unknown; value?: unknown };
        return String(entry.text ?? entry.value ?? '');
      }
      return String(item ?? '');
    })
    .map((value) => value.trim())
    .filter((value) => value !== '');

  const text = source.text ?? source.body ?? null;
  return {
    name,
    language: (source.language ?? source.lang ?? null) as string | null,
    params,
    text: text ? String(text) : null,
  };
};

const normalizeMessage = (message: WhatsappMessage): WhatsappMessage => {
  const ours = message.reactions?.find((reaction) => reaction.direction === 'out');
  return {
    ...message,
    template: normalizeTemplate(message),
    body: message.body ?? null,
    from_user_name: message.from_user_name || message.from_user?.name || null,
    our_reaction: message.our_reaction ?? ours?.emoji ?? null,
    our_reaction_user_id: message.our_reaction_user_id ?? ours?.from_user_id ?? null,
    our_reaction_user_name: message.our_reaction_user_name ?? ours?.from_user_name ?? null,
  };
};

type SilentConfig = AxiosRequestConfig & { skipErrorToast?: boolean };

const basePath = 'galidancia/whatsapp';

const silent = (config: SilentConfig = {}): SilentConfig => ({
  ...config,
  skipErrorToast: true,
});

export abstract class WhatsappGalidanciaService {
  static async listConversations(params: { page: number; per_page: number; q?: string }) {
    const response = await axios.get<WhatsappConversationPage>(`${basePath}/conversations`, silent({
      params: {
        page: params.page,
        per_page: params.per_page,
        q: params.q || undefined,
        session_open: 1,
      },
    }));
    return response.data;
  }

  static async listMessages(
    conversationId: number,
    params: { limit: number; before_id?: number; after_id?: number },
  ) {
    const response = await axios.get<WhatsappMessagePage>(
      `${basePath}/conversations/${conversationId}/messages`,
      silent({
        params: {
          limit: params.limit,
          before_id: params.before_id,
          after_id: params.after_id,
        },
      }),
    );
    const body = response.data as {
      data?: WhatsappMessage[] | { data?: WhatsappMessage[]; has_more?: boolean };
      messages?: WhatsappMessage[];
      has_more?: boolean;
    } | WhatsappMessage[];
    const nested = Array.isArray(body) ? undefined : body?.data;
    const rows = Array.isArray(body)
      ? body
      : Array.isArray(nested)
        ? nested
        : nested && Array.isArray(nested.data)
          ? nested.data
          : !Array.isArray(body) && Array.isArray(body?.messages)
            ? body.messages
            : [];
    const hasMore = Array.isArray(body)
      ? false
      : Boolean(body?.has_more || (nested && !Array.isArray(nested) ? nested.has_more : false));
    return {
      data: rows.map(normalizeMessage),
      has_more: hasMore,
    };
  }

  static async sendMessage(conversationId: number, text: string) {
    const response = await axios.post<WhatsappMessage>(
      `${basePath}/conversations/${conversationId}/messages`,
      { text },
      silent(),
    );
    return normalizeMessage(response.data);
  }

  /**
   * Envía un adjunto (imagen, vídeo, audio o documento) con texto opcional como pie.
   * Mismo endpoint que el texto, pero en multipart: el API deduce el tipo del fichero.
   */
  static async sendMedia(conversationId: number, file: File, text?: string) {
    const form = new FormData();
    form.append('file', file);
    if (text) form.append('text', text);
    const response = await axios.post<WhatsappMessage>(
      `${basePath}/conversations/${conversationId}/messages`,
      form,
      silent({ headers: { 'Content-Type': 'multipart/form-data' } }),
    );
    return normalizeMessage(response.data);
  }

  static async fetchMedia(messageId: number): Promise<WhatsappMediaSource> {
    const response = await axios.get(`${basePath}/messages/${messageId}/media`, silent({
      responseType: 'blob',
    }));
    const blob = response.data as Blob;
    const type = blob.type || '';
    if (type.includes('json') || type.startsWith('text/')) {
      const payload = JSON.parse(await blob.text()) as { url?: string; media_url?: string; data?: { url?: string } };
      const url = payload.url || payload.media_url || payload.data?.url;
      if (!url) throw new Error('Media sin url');
      return { url, revoke: false };
    }
    return { url: URL.createObjectURL(blob), revoke: true };
  }

  static async react(conversationId: number, messageId: number, emoji: string) {
    const response = await axios.post<WhatsappMessage>(
      `${basePath}/conversations/${conversationId}/messages/${messageId}/reaction`,
      { emoji },
      silent(),
    );
    return normalizeMessage(response.data);
  }

  static async markRead(conversationId: number) {
    await axios.post(`${basePath}/conversations/${conversationId}/read`, {}, silent());
  }
}
